/**
 * ListingCard Component
 *
 * Displays a marketplace listing in a card format for the discovery page.
 */

import React from 'react';
import { ArrowDownTrayIcon, TagIcon } from '@heroicons/react/24/outline';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import type { MarketplaceListing } from '../../../types/marketplace.types';
import { MARKETPLACE_CATEGORIES } from '../../../types/marketplace.types';

interface ListingCardProps {
  listing: MarketplaceListing;
  onView: (listing: MarketplaceListing) => void;
  onInstall?: (listing: MarketplaceListing) => void;
  isInstalling?: boolean;
}

/**
 * Get display label for a category
 */
const getCategoryLabel = (category: string): string => {
  const found = MARKETPLACE_CATEGORIES.find((c) => c.value === category);
  return found?.label || category;
};

/**
 * ListingCard component
 */
export const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  onView,
  onInstall,
  isInstalling,
}) => {
  const latestVersion = listing.availableVersions?.[listing.availableVersions.length - 1];

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          {listing.iconUrl ? (
            <img
              src={listing.iconUrl}
              alt={listing.name}
              className="w-12 h-12 rounded-lg object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
              <span className="text-primary-600 font-semibold text-lg">
                {listing.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{listing.name}</h3>
            <p className="text-sm text-gray-500">by {listing.publisherTenantCode}</p>
          </div>
        </div>
        <Badge variant="secondary" size="sm">
          <TagIcon className="w-3 h-3 mr-1" />
          {getCategoryLabel(listing.category)}
        </Badge>
      </div>

      {/* Description */}
      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
        {listing.description}
      </p>

      {/* Stats */}
      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
        <span className="flex items-center">
          <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
          {listing.installCount.toLocaleString()} installs
        </span>
        {latestVersion && (
          <span>{latestVersion.version}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onView(listing)}
          className="flex-1"
        >
          View Details
        </Button>
        {onInstall && (
          <Button
            size="sm"
            onClick={() => onInstall(listing)}
            disabled={isInstalling}
            className="flex-1"
          >
            {isInstalling ? 'Installing...' : 'Install'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ListingCard;
